import { useCallback, useMemo } from 'react';

import { countActiveRateFilters, DEFAULT_RATE_FILTERS, filterCarrierRows } from './rate-filtering';
import { groupRates } from './rate-grouping';
import { searchCarrierRows } from './rate-search';
import { sortCarrierRows } from './rate-sorting';
import { useRateFilters } from './use-rate-filters';
import { useRateSearch } from './use-rate-search';
import { useRateSortOption } from './use-rate-sort-option';

import type { CarrierRateRow } from './freight-rate-card-types';
import type { RateFilters } from './rate-filtering';
import type { RateSortOption } from './rate-sorting';
import type { LtlQuote } from '../../types/ltl';

type UseRateRowsOptions = {
  rates: LtlQuote[];
  isInsured: boolean;
  isLoading: boolean;
};

type UseRateRowsReturn = {
  carrierRows: CarrierRateRow[];
  sortOption: RateSortOption;
  setSortOption: (next: RateSortOption) => void;
  filters: RateFilters;
  setFilters: (next: RateFilters) => void;
  clearRateFilters: () => void;
  hasFilteredOutAllRates: boolean;
  searchTerm: string;
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  collapseEmptySearch: () => void;
  handleSearchChange: (value: string) => void;
};

export const useRateRows = ({ rates, isInsured, isLoading }: UseRateRowsOptions): UseRateRowsReturn => {
  const [sortOption, setSortOption] = useRateSortOption();
  const [filters, setFilters] = useRateFilters();
  const { searchTerm, isSearchOpen, openSearch, closeSearch, collapseEmptySearch, handleSearchChange } = useRateSearch();

  const clearRateFilters = useCallback(() => {
    setFilters(DEFAULT_RATE_FILTERS);
    closeSearch();
  }, [setFilters, closeSearch]);

  const grouped = useMemo(() => groupRates(rates), [rates]);
  const unsortedCarrierRows = isInsured ? grouped.withInsurance : grouped.withoutInsurance;
  const filteredCarrierRows = useMemo(() => filterCarrierRows(unsortedCarrierRows, filters), [unsortedCarrierRows, filters]);
  const searchedCarrierRows = useMemo(() => searchCarrierRows(filteredCarrierRows, searchTerm), [filteredCarrierRows, searchTerm]);
  const carrierRows = useMemo(() => sortCarrierRows(searchedCarrierRows, sortOption), [searchedCarrierRows, sortOption]);

  const hasActiveFilters = countActiveRateFilters(filters) > 0;
  const hasFilteredOutAllRates = !isLoading && rates.length > 0 && carrierRows.length === 0 && (hasActiveFilters || searchTerm !== '');

  return {
    carrierRows,
    sortOption,
    setSortOption,
    filters,
    setFilters,
    clearRateFilters,
    hasFilteredOutAllRates,
    searchTerm,
    isSearchOpen,
    openSearch,
    closeSearch,
    collapseEmptySearch,
    handleSearchChange,
  };
};
